import React, { useEffect, useRef, useState } from "react";
import {
    IonPage,
    IonContent,
    IonGrid,
    IonRow,
    IonCol,
    IonCardContent,
    IonCard,
    IonLabel,
    IonIcon,
    IonLoading,
    useIonModal,
    IonToolbar,
    IonHeader,
    IonTitle,
    IonText,
    useIonAlert
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import './Hub.css';
import {
    giftOutline,
    phonePortraitOutline,
    storefrontOutline,

} from "ionicons/icons";
import { useAppDispatch, useAppSelector } from '../../services/redux/hook';
import { Asset, DestinationUserName, PaymentDestinationInfo, UserInfo, WalletInfoState } from "../../types/types";
import GiftCardProviderPage from "../gitfcards/GiftCardProviderPage";
import TopupRecipient from "../topups/TopupRecipient";
import BaseModalComponent from "../../components/BaseModalComponent/BaseModalComponent";
import { openModal } from "../../services/redux/slices/opened_navigation_modal/opened_root_modal";
import { useHistory } from 'react-router-dom';
import ShopPage from "../shop/ShopPage";
import AmountPage from "../amount/AmountPage";
import SendPage from "../send/SendPage";
import { isStellarAccount } from "@/services/stellar/stellar-utils";
import { resetPaymentDestinationInfo, setPaymentDestinationInfo } from "@/services/redux/slices/paymentDestinationInfo/paymentDestinationInfo";
import { isStellarUri, parseStellarUri } from "@/services/stellar/stellar-uri/parse";
import { PayStellarUri } from "@/services/stellar/stellar-uri/pay-stellar-uri";
import * as walletSdk from '@stellar/typescript-wallet-sdk';
import { setSelectedAsset } from "@/services/redux/slices/selected-asset/selected-asset";
import { setOpenedSep7Uri } from "@/services/redux/slices";

const Hub: React.FC<any> = () => {

    const { t } = useTranslation();
    const history = useHistory();
    const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);
    const supportedAssets: Asset[] = useAppSelector((state) => state.supportedAssets.supportedAssets);
    const selectedAsset: Asset = useAppSelector((state) => state.selectedAsset);
    const openedSep7Uri = useAppSelector((state) => state.openedSep7Uri);
    const [showLoading, setShowLoading] = useState(false);
    const pageRef = useRef();
    const dispatch = useAppDispatch();
    const [presentAlert] = useIonAlert();

    const [presentGiftModal, dismissGiftModal] = useIonModal(BaseModalComponent, { component: GiftCardProviderPage });
    const [presentTopUpModal, dismissTopUpModal] = useIonModal(BaseModalComponent, { component: TopupRecipient });
    const [presentShopModal, dismissShopModal] = useIonModal(BaseModalComponent, { component: ShopPage });
    const [showAmountPage, dismissAmountPage] = useIonModal(BaseModalComponent, { component: AmountPage });
    const [showSendPage, dismissSendPage] = useIonModal(BaseModalComponent, { component: SendPage });


    const presentGiftCardModal = async () => {
        dispatch(openModal({ dismissFunc: dismissGiftModal }));
        presentGiftModal({});
    }

    const presentTopUpRecipient = async () => {
        dispatch(openModal({ dismissFunc: dismissTopUpModal }));
        presentTopUpModal({});
    }

    const presentCardShopModal = async () => {
        dispatch(openModal({ dismissFunc: dismissShopModal }));
        presentShopModal({});
    }

    const presentErrorAlert = (error: any) => {
        presentAlert({
            header: 'Error',
            message: `${error.data}`,
            buttons: [{ text: 'Close', role: 'cancel' }],
        });
    };

    useEffect(() => {
        if (!walletInfo?.account_key) {
            history.replace("/");
        }
    }, [walletInfo, history]);

    useEffect(() => {
        if (openedSep7Uri) {
            // 1. Close any open modal first
            dismissShopModal();

            // 2. Now handle the URI
            setTimeout(() => {
                handleSep7OpenUri();
            }, 300);
        }
    }, [openedSep7Uri]);

    // SEP-7 URI handler (same logic as AccountsPage)
    const handleSep7OpenUri = async () => {
        try {
            const uriResult = walletSdk.parseSep7Uri(openedSep7Uri);

            if (!(uriResult instanceof walletSdk.Sep7Pay)) {
                presentErrorAlert(`This link is not supported.`);
                return;
            }

            const passesVerification = await uriResult.verifySignature();

            if (!passesVerification) {
                presentErrorAlert(`This link is not supported.`);
                return;
            } else {
                validatepaymentLinkOrQrContent(openedSep7Uri);
            }
        } catch (error) {
            console.error('Error handling SEP-7 URI:', error);
            presentErrorAlert('Error processing payment link');
        }
    };

    // Payment validation and modal navigation (adapted from AccountsPage)
    const validatepaymentLinkOrQrContent = async (content: string) => {
        if (isStellarAccount(content)) {
            const response = await validatePaymentLink(content);
            if (response) {
                dispatch(resetPaymentDestinationInfo());
                const destinationInfo: PaymentDestinationInfo = {
                    destination_address: content,
                    destination_asset: undefined,
                    destination_amount: undefined,
                };
                dispatch(setPaymentDestinationInfo(destinationInfo));
                goToAmountPage();
                return;
            }
        }

        if (isStellarUri(content)) {
            const uri = parseStellarUri(content);
            if (uri instanceof PayStellarUri) {
                if (!uri.destination) {
                    const error = t("accounts.payment.link.NO-DESTINATION-ACCOUNT");
                    presentErrorAlert(error);
                    return;
                }

                const response = await validatePaymentLink(uri);
                if (response) {
                    let destinationAsset = selectedAsset;

                    // Find the correct asset
                    if (uri.assetCode && uri.assetIssuer) {
                        const foundAsset = supportedAssets.find(asset =>
                            asset.identifier === `${uri.assetCode}:${uri.assetIssuer}`
                        );
                        if (foundAsset) {
                            destinationAsset = foundAsset;
                        }
                    } else if (!uri.assetCode) {
                        const nativeAsset = supportedAssets.find(asset => asset.identifier === 'native');
                        if (nativeAsset) {
                            destinationAsset = nativeAsset;
                        }
                    }

                    const destinationInfo: PaymentDestinationInfo = {
                        destination_address: uri.destination,
                        destination_asset: destinationAsset,
                        destination_amount: uri.amount ? parseFloat(uri.amount).toFixed(2) : undefined,
                        destination_memo: uri.memo ? uri.memo : undefined,
                        destination_memo_type: uri.memo ? 'text' : undefined,
                        message: uri.msg ? decodeURI(uri.msg) : undefined
                    };

                    if (uri.name) {
                        let destination_username: DestinationUserName = {
                            name: decodeURI(uri.name),
                            username: t('barcode.page.merchant-place-holder') as string
                        };
                        destinationInfo.destination_username = destination_username;
                    }

                    dispatch(setPaymentDestinationInfo(destinationInfo));
                    dispatch(setSelectedAsset(destinationAsset));

                    // Navigate to appropriate page based on amount
                    if (uri.amount) {
                        goToSendPage();
                    } else {
                        goToAmountPage();
                    }

                    // Clear the URI after handling
                    dispatch(setOpenedSep7Uri(''));
                }
            }
        }
    };

    // Placeholder validation function (you may need to implement this)
    const validatePaymentLink = async (destination: any): Promise<boolean> => {
        // Add your validation logic here
        // For now, return true to allow the flow to continue
        return true;
    };

    // Modal navigation functions (consistent with AccountsPage pattern)
    const goToAmountPage = () => {
        dispatch(openModal({ dismissFunc: dismissAmountPage }));

        setTimeout(() => {
            showAmountPage({
                presentingElement: pageRef.current,
            });
        }, 100);
    };

    const goToSendPage = () => {
        dispatch(openModal({ dismissFunc: dismissSendPage }));

        setTimeout(() => {
            showSendPage({
                presentingElement: pageRef.current,
            });
        }, 200);
    };

    // Define mini apps with icons and colors
    const miniApps = [
        {
            icon: phonePortraitOutline,
            label: t('more.page.Topups-LABEL') as string,
            color: 'primary',
            action: presentTopUpRecipient,
            enabled: true
        },
        {
            icon: giftOutline,
            label: t('more.page.Gifts-LABEL') as string,
            color: 'warning',
            action: presentGiftCardModal,
            enabled: true
        },
        {
            icon: storefrontOutline,
            label: t('more.page.Shop-LABEL') as string,
            color: 'success',
            action: presentCardShopModal,
            enabled: true
        },
        // { 
        //     icon: carOutline, 
        //     label: 'Taxi', 
        //     color: 'secondary',
        //     action: () => {},
        //     enabled: false
        // },
        // { 
        //     icon: calendarOutline, 
        //     label: 'Bookings', 
        //     color: 'tertiary',
        //     action: () => {},
        //     enabled: false
        // },
        // { 
        //     icon: restaurantOutline, 
        //     label: 'Food', 
        //     color: 'danger',
        //     action: () => {},
        //     enabled: false
        // },
        // { 
        //     icon: airplaneOutline, 
        //     label: 'Travel', 
        //     color: 'primary',
        //     action: () => {},
        //     enabled: false
        // },
        // { 
        //     icon: flashOutline, 
        //     label: 'Bills', 
        //     color: 'warning',
        //     action: () => {},
        //     enabled: false
        // },
        // { 
        //     icon: bulbOutline, 
        //     label: 'Electricity', 
        //     color: 'tertiary',
        //     action: () => {},
        //     enabled: false
        // },
        // { 
        //     icon: wifiOutline, 
        //     label: 'Internet', 
        //     color: 'secondary',
        //     action: () => {},
        //     enabled: false
        // },
        // { 
        //     icon: tvOutline, 
        //     label: 'Entertainment', 
        //     color: 'success',
        //     action: () => {},
        //     enabled: false
        // },
        // { 
        //     icon: homeOutline, 
        //     label: 'Rent', 
        //     color: 'danger',
        //     action: () => {},
        //     enabled: false
        // },
    ];

    return (
        <IonPage ref={pageRef}>
            <IonHeader className="ion-no-border">
                <IonToolbar>
                    <IonTitle>{t('more.page.header') as string}</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent scrollY={false}>
            
                <IonLoading isOpen={showLoading} />

                <div className="max-w-2xl mx-auto" style={{ maxWidth: '768px', margin: '10%' }}>

                    {/* Rewards Card */}
                    {/* <IonCard 
                        button 
                        className="rewards-card"
                        style={{
                            background: 'linear-gradient(135deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 50%, var(--ion-color-primary-tint) 100%)',
                            marginBottom: '24px',
                            borderRadius: '16px',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                        }}
                    >
                        <IonCardContent style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div 
                                        style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '12px',
                                            background: 'rgba(255,255,255,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <IonIcon icon={ribbonOutline} style={{ fontSize: '24px', color: 'white' }} />
                                    </div>
                                    <div>
                                        <IonText style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                            Rewards
                                        </IonText>
                                        <IonText style={{ color: 'white', fontSize: '28px', fontWeight: 'bold', display: 'block' }}>
                                            {totalPoints.toLocaleString()}
                                        </IonText>
                                    </div>
                                </div>
                                <IonIcon icon={starOutline} style={{ fontSize: '32px', color: '#FFD700' }} />
                            </div>
                            <IonText style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px', display: 'block', marginTop: '16px' }}>
                                Earn more points with every transaction
                            </IonText>
                        </IonCardContent>
                    </IonCard> */}

                    {/* Apps Grid */}
                    <IonGrid style={{ padding: 0 }}>
                        <IonRow>
                            {miniApps.map((app, index) => (
                                <IonCol size="6" key={index} style={{ padding: '8px' }}>
                                    <IonCard
                                        button={app.enabled}
                                        disabled={!app.enabled}
                                        onClick={app.enabled ? app.action : undefined}
                                        style={{
                                            margin: 0,
                                            borderRadius: '16px',
                                            height: '100%',
                                            opacity: app.enabled ? 1 : 0.5,
                                            transition: 'all 0.3s ease',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.08)'
                                        }}
                                        className="mini-app-card"
                                    >
                                        <IonCardContent style={{ padding: '20px', height: '100%' }}>
                                            <div
                                                className={`mini-app-icon mini-app-icon-${app.color}`}
                                                style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    borderRadius: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginBottom: '16px',
                                                    transition: 'transform 0.3s ease'
                                                }}
                                            >
                                                <IonIcon icon={app.icon} style={{ fontSize: '24px', color: 'white' }} />
                                            </div>
                                            <IonLabel>
                                                <h3 className="ion-text-start" style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px' }}>
                                                    {app.label}
                                                </h3>
                                                {!app.enabled && (
                                                    <IonText color="medium" style={{ fontSize: '12px' }}>
                                                        Coming soon
                                                    </IonText>
                                                )}
                                            </IonLabel>
                                        </IonCardContent>
                                    </IonCard>
                                </IonCol>
                            ))}
                        </IonRow>
                    </IonGrid>
                </div>
            </IonContent>
        </IonPage>
    )
}

export default React.memo(Hub);
